import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as z from 'zod/v4';
import { useMutation } from '@tanstack/react-query';
import RegistrationService from '@app/model/registration/service/RegistrationService';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useTranslation } from '@app/component/translation/Translation';
import useSiteState from '@app/state/SiteState';
import Principal from '@app/model/authentication/Principal';
import useAppForm from '@app/site/_form';
import { HotelField } from '@app/site/_form/field/HotelField';
import { SubmitButton } from '@app/site/_form/button/SubmitButton';


const ParityUserCreateCompanyHotelPage = React.memo(() => {

	const toaster = useToaster();
	const { t } = useTranslation();
	const { setPrincipal } = useSiteState();
	const navigate = useNavigate();


	const createParityCompanyHotelMutation = useMutation({
		mutationFn: RegistrationService.createParityCompanyHotel,
		onSuccess: (principal: Principal) => {
			setPrincipal(principal);
			navigate('/registration');
		},
		onError: (error: string) => {
			toaster.error('Registration', t(error));
		},
	});

	const createParityCompanyHotelSchema = z.object({
		hotelId: z.number().gte(1, 'Hotel is required.'),
		webhotelierId: z.string({ error: 'Webhotelier Id is required.' }).min(1),
	});

	const form = useAppForm({
		defaultValues: {
			hotelId: 0,
			webhotelierId: '',
		},
		validators: {
			onChange: createParityCompanyHotelSchema,
		},
		onSubmit: ({ value }) => createParityCompanyHotelMutation.mutateAsync(value).catch(() => {
		}),

	});

	return <div className="min-h-full bg-white">
		<div className="flex lg:h-dvh content-center justify-center flex-1">
			<div className="flex flex-1 flex-col justify-center px-6 py-12">
				<div className="mx-auto w-full max-w-sm sm:max-w-2xl">
					<div>
						<Link to="https://www.roompulse.io">
							<img
								className="h-10 w-auto"
								src="/assets/img/brand/logo-icon.png"
								alt="RoomPulse"
							/>
						</Link>
						<div className="mt-6">
							<h2 className="text-2xl font-bold leading-9 tracking-tight text-gray-900">Add Property</h2>
						</div>

						{/* Properties */}
						<div className="mt-4 text-sm leading-6">
							<form.AppForm>
								<form className="bg-gray-50 shadow-sm ring-1 ring-gray-900/10 rounded-xl md:col-span-2"
											onSubmit={(e) => {
												e.preventDefault();
												e.stopPropagation();
											}}>
									{/* Header */}
									<div className="gap-x-6 border-b border-gray-900/10 px-4 py-5 sm:px-8">
										<h3 className="text-base font-semibold leading-7 text-gray-900">Add property</h3>
									</div>
									{/* Body */}
									<div className="p-4 sm:p-8">
										{/* Your Hotel */}
										<div>
											<form.AppField name="hotelId">
												{(field) => <HotelField placeholder="Insert property name" />}
											</form.AppField>
										</div>
										{/* Your Webhotelier ID */}
										<div className="mt-4">
											<div className="flex">
												<div
													className="flex shadow-sm shrink-0 items-center rounded-l-md bg-white px-3 text-gray-600 outline outline-1 -outline-offset-1 outline-gray-300 text-sm/6">
													Webhotelier ID
												</div>
												<form.AppField name="webhotelierId">
													{(field) => <field.TextField extraClassName="-ml-px rounded-l-none w-full"
																											 placeholder="Insert Webhotelier ID" />}
												</form.AppField>
											</div>
										</div>
									</div>
									{/* Footer Actions */}
									<div className="flex items-center justify-end gap-x-2 border-t border-gray-900/10 px-4 py-4 sm:px-8">
										<SubmitButton state="primary">Submit</SubmitButton>
									</div>
								</form>
							</form.AppForm>
						</div>
					</div>
				</div>
			</div>
			<div className="relative hidden flex-1 lg:block">
				<img
					className="absolute inset-0 h-full w-full object-cover object-left"
					src="https://images.unsplash.com/photo-1722409194949-6d15fe881152?q=80&w=1548&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
					alt=""
				/>
			</div>
		</div>
	</div>;
});

export default ParityUserCreateCompanyHotelPage;